import React, { useState, useEffect } from 'react';
import { Search, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import api from '../services/api';
import './GoalSearch.css';

const GoalSearch = ({ onSelect, onCancel, platform, existingGoalIds = [] }) => {
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [selectedGoals, setSelectedGoals] = useState([]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchGoals();
    }, [pagination.page, debouncedSearchTerm, platform]);

    const fetchGoals = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'createdAt',
                sortOrder: 'DESC'
            };

            if (debouncedSearchTerm) params.search = debouncedSearchTerm;
            if (platform) params.platform = platform;

            const response = await api.get('/tests/goals', { params });
            setGoals(response.data.goals);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching goals:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleGoal = (goal) => {
        if (existingGoalIds.includes(goal.id)) return;
        
        setSelectedGoals(prev => {
            const isSelected = prev.find(g => g.id === goal.id);
            if (isSelected) {
                return prev.filter(g => g.id !== goal.id);
            } else {
                return [...prev, goal];
            }
        });
    };

    const handleConfirm = () => {
        onSelect(selectedGoals);
    };

    return (
        <div className="goal-search">
            <div className="goal-search__filters">
                <div className="goal-search__search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search existing goals..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                        autoFocus
                    />
                </div>
            </div>

            <div className="goal-search__list">
                {loading ? (
                    <div className="goal-search__loading">
                        <Loader2 className="animate-spin" size={24} />
                        <span>Searching goals...</span>
                    </div>
                ) : goals.length === 0 ? (
                    <div className="goal-search__empty">
                        <p>No goals found matching your search</p>
                    </div>
                ) : (
                    <div className="goal-search__items">
                        {goals.map((goal) => {
                            const isSelected = selectedGoals.find(g => g.id === goal.id);
                            const isAlreadyInPlan = existingGoalIds.includes(goal.id);
                            
                            return (
                                <div 
                                    key={goal.id} 
                                    className={`goal-search__item ${isSelected ? 'goal-search__item--selected' : ''} ${isAlreadyInPlan ? 'goal-search__item--disabled' : ''}`}
                                    onClick={() => toggleGoal(goal)}
                                >
                                    <div className="goal-search__item-checkbox">
                                        {(isSelected || isAlreadyInPlan) && <Check size={14} />}
                                    </div>
                                    <div className="goal-search__item-content">
                                        <div className="goal-search__item-text">{goal.goalText}</div>
                                        <div className="goal-search__item-meta">
                                            {goal.platform === 'ios' ? '🍎 iOS' : '🤖 Android'} 
                                            {isAlreadyInPlan && <span className="goal-search__already-in-plan">• Already in plan</span>}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="goal-search__footer">
                <div className="goal-search__pagination">
                    {pagination.totalPages > 1 && (
                        <div className="goal-search__pagination-controls">
                            <button
                                className="goal-search__pagination-btn"
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                disabled={pagination.page === 1}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span>{pagination.page} / {pagination.totalPages}</span>
                            <button
                                className="goal-search__pagination-btn"
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                disabled={pagination.page >= pagination.totalPages}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
                <div className="goal-search__actions">
                    <button className="execution-plan-edit__btn execution-plan-edit__btn--secondary" onClick={onCancel}>
                        Cancel
                    </button>
                    <button 
                        className="execution-plan-edit__btn execution-plan-edit__btn--primary" 
                        onClick={handleConfirm}
                        disabled={selectedGoals.length === 0}
                    >
                        Add Selected ({selectedGoals.length})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GoalSearch;
